import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { PJC_STRUCTURE, DEFAULT_PJC_DATA } from "@/lib/prejobStructure";
import SignaturePad from "@/components/SignaturePad";
import { Loader2, ClipboardCheck, AlertTriangle, Clock, Send, ArrowRight, Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

// ─────────────── List page ───────────────
export function PreJobChecksList() {
  const { user } = useAuth();
  const [box, setBox] = useState("received");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pre-job-checks", { params: { box } });
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [box]);

  return (
    <div className="space-y-4" data-testid="pjc-list">
      <header>
        <div className="label-up text-[#0E78C2] inline-flex items-center gap-1.5">
          <ClipboardCheck size={12} /> Pre-Job Check
        </div>
        <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">Formularios PRL</h1>
        <p className="text-zinc-600 text-sm mt-1">Recibidos y enviados por {user?.name}</p>
      </header>

      <div className="flex gap-2 text-xs font-semibold">
        {[
          ["received", "Recibidos"],
          ["sent", "Enviados"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setBox(k)}
            data-testid={`pjc-tab-${k}`}
            className={`px-4 py-2 rounded-full transition-colors ${
              box === k ? "bg-[#0B2A4A] text-white" : "bg-slate-100 text-slate-600"
            }`}
          >{l}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-400"><Loader2 className="animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <div className="brutal-card p-6 text-center text-slate-500 text-sm">Sin formularios en esta bandeja.</div>
      ) : (
        <ul className="space-y-2" data-testid="pjc-items">
          {items.map((p) => (
            <Link
              key={p.id}
              to={`/pre-job-checks/${p.id}`}
              data-testid={`pjc-item-${p.id}`}
              className="brutal-card p-4 flex items-center gap-3 hover:shadow-lg transition-all"
            >
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                p.status === "signed" ? "bg-emerald-100 text-emerald-700"
                : p.status === "rejected" ? "bg-rose-100 text-rose-700"
                : "bg-amber-100 text-amber-700"
              }`}>
                {p.status === "signed" ? <Check size={20} /> : p.status === "rejected" ? <X size={20} /> : <Clock size={20} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">
                  {box === "received" ? `De ${p.sender_name}` : `Para ${p.recipient_name}`}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {p.obra || p.trabajo || "Pre-job check"} · {new Date(p.created_at).toLocaleDateString("es-ES")}
                </div>
              </div>
              <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${
                p.status === "signed" ? "bg-emerald-500/15 text-emerald-700"
                : p.status === "rejected" ? "bg-rose-500/15 text-rose-700"
                : "bg-amber-500/15 text-amber-700"
              }`}>
                {p.status === "signed" ? "Firmado" : p.status === "rejected" ? "Rechazado" : "Pendiente"}
              </span>
              <ArrowRight size={16} className="text-slate-400 flex-shrink-0" />
            </Link>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─────────────── Detail / fill / sign page ───────────────
export function PreJobCheckDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [pjc, setPjc] = useState(null);
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const sigRef = useRef(null);

  useEffect(() => {
    api.get(`/pre-job-checks/${id}`).then((r) => {
      setPjc(r.data);
      setData({ ...DEFAULT_PJC_DATA(), ...(r.data.data || {}) });
    }).catch(() => toast.error("No se pudo cargar el formulario."));
  }, [id]);

  const isRecipient = pjc && user && pjc.recipient_id === user.id;
  const isPending = pjc?.status === "pending";
  const readOnly = !isRecipient || !isPending;

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const shouldShow = (f) => {
    if (!f.showIf) return true;
    return String(data[f.showIf.key] || "") === String(f.showIf.equals);
  };

  const submit = async () => {
    // Basic validation of required yes/no
    for (const sec of PJC_STRUCTURE) {
      for (const f of sec.fields) {
        if (f.required && !data[f.key]) {
          toast.error(`Falta responder: "${f.label}"`);
          return;
        }
      }
    }
    const sig = sigRef.current?.getDataURL();
    if (!sig) { toast.error("Firma requerida."); return; }
    setSaving(true);
    try {
      await api.post(`/pre-job-checks/${id}/sign`, { signature: sig, data });
      toast.success("Firmado correctamente.");
      nav("/pre-job-checks");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo firmar.");
    } finally { setSaving(false); }
  };

  const reject = async () => {
    const reason = window.prompt("Motivo del rechazo (opcional):") || "";
    setSaving(true);
    try {
      await api.post(`/pre-job-checks/${id}/reject`, { reason, data });
      toast.success("Rechazado.");
      nav("/pre-job-checks");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo rechazar.");
    } finally { setSaving(false); }
  };

  if (!pjc) return <div className="text-center py-10 text-slate-400"><Loader2 className="animate-spin inline" /></div>;

  return (
    <div className="space-y-5 pb-24" data-testid="pjc-detail">
      <header className="brutal-card p-4">
        <div className="label-up text-[#0E78C2] inline-flex items-center gap-1.5">
          <ClipboardCheck size={12} /> Pre-Job Check
        </div>
        <h1 className="font-display font-extrabold text-2xl leading-tight mt-1">
          {pjc.obra || pjc.trabajo || "Formulario PRL"}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          De <strong>{pjc.sender_name}</strong> → Para <strong>{pjc.recipient_name}</strong>
        </p>
        <div className="mt-2">
          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${
            pjc.status === "signed" ? "bg-emerald-500/15 text-emerald-700"
            : pjc.status === "rejected" ? "bg-rose-500/15 text-rose-700"
            : "bg-amber-500/15 text-amber-700"
          }`}>{pjc.status === "signed" ? "Firmado" : pjc.status === "rejected" ? "Rechazado" : "Pendiente"}</span>
        </div>
      </header>

      {PJC_STRUCTURE.map((sec) => (
        <section key={sec.id} className="brutal-card p-4 space-y-3">
          <div>
            <h2 className="font-display font-extrabold text-lg text-[#0B2A4A]">{sec.title}</h2>
            {sec.hint && <p className="text-xs text-slate-500 mt-0.5">{sec.hint}</p>}
            {sec.warning && (
              <div className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2 inline-flex items-start gap-1.5">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" /><span>{sec.warning}</span>
              </div>
            )}
          </div>
          <div className="space-y-3">
            {sec.fields.filter(shouldShow).map((f) => (
              <FormField key={f.key} field={f} value={data[f.key]} onChange={(v) => update(f.key, v)} disabled={readOnly} />
            ))}
          </div>
        </section>
      ))}

      <section className="brutal-card p-4 space-y-3">
        <h2 className="font-display font-extrabold text-lg text-[#0B2A4A]">Firma del receptor</h2>
        {pjc.signature_url ? (
          <div className="border rounded-xl bg-white p-2">
            <img src={pjc.signature_url} alt="Firma" className="max-h-40 mx-auto" data-testid="pjc-signature-image" />
            {pjc.signed_at && (
              <p className="text-[11px] text-slate-500 text-center mt-2">
                Firmado el {new Date(pjc.signed_at).toLocaleString("es-ES")}
              </p>
            )}
          </div>
        ) : readOnly ? (
          <p className="text-sm text-slate-500">Sin firma.</p>
        ) : (
          <SignaturePad ref={sigRef} />
        )}
      </section>

      {isPending && isRecipient && (
        <div className="fixed bottom-16 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200 p-3 flex gap-2 max-w-3xl mx-auto">
          <button
            type="button"
            onClick={reject}
            disabled={saving}
            data-testid="pjc-reject"
            className="flex-1 h-11 rounded-lg border border-rose-300 text-rose-600 font-semibold text-sm inline-flex items-center justify-center gap-1.5"
          >
            <X size={16} /> Rechazar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            data-testid="pjc-sign"
            className="flex-[2] h-11 rounded-lg bg-gradient-to-br from-[#FF8A1F] to-[#F5A623] text-white font-semibold text-sm inline-flex items-center justify-center gap-1.5"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Firmar y aceptar
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────── Form field renderer ───────────────
function FormField({ field, value, onChange, disabled }) {
  if (field.type === "text" || field.type === "date" || field.type === "time") {
    return (
      <div>
        <label className="label-up block mb-1">{field.label}</label>
        <input
          type={field.type} className="brutal-input w-full disabled:bg-slate-50"
          placeholder={field.placeholder || ""} disabled={disabled}
          value={value || ""} onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div>
        <label className="label-up block mb-1">{field.label}</label>
        <textarea
          className="brutal-input w-full min-h-[70px] disabled:bg-slate-50" rows={3}
          placeholder={field.placeholder || ""} disabled={disabled}
          value={value || ""} onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  }
  if (field.type === "check") {
    return (
      <label className="flex items-start gap-2 text-sm cursor-pointer">
        <input
          type="checkbox" className="h-5 w-5 rounded border-slate-300 text-[#F5A623] mt-0.5"
          disabled={disabled}
          checked={!!value} onChange={(e) => onChange(e.target.checked)}
        />
        <span className="text-slate-700 leading-tight">{field.label}</span>
      </label>
    );
  }
  if (field.type === "boolean") {
    return (
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input
          type="checkbox" className="h-5 w-5 rounded border-slate-300 text-[#F5A623]"
          disabled={disabled}
          checked={!!value} onChange={(e) => onChange(e.target.checked)}
        />
        <span className="text-slate-700 font-semibold">{field.label}</span>
      </label>
    );
  }
  if (field.type === "yesno") {
    return (
      <div>
        <label className="text-sm text-slate-700 block mb-1.5 leading-tight">
          {field.label} {field.required && <span className="text-rose-500">*</span>}
        </label>
        <div className="flex gap-2">
          {["SI", "NO"].map((opt) => (
            <button
              key={opt} type="button" disabled={disabled}
              onClick={() => onChange(opt)}
              className={`flex-1 h-10 rounded-lg text-sm font-bold transition-colors ${
                value === opt
                  ? opt === "SI" ? "bg-emerald-600 text-white" : "bg-rose-600 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              } disabled:opacity-60`}
            >{opt}</button>
          ))}
        </div>
      </div>
    );
  }
  return null;
}

// ─────────────── Send-modal (invoked from NewWorkLog) ───────────────
export function SendPreJobCheckModal({ open, onOpenChange, workLogId, obra, trabajo, onSent }) {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [sending, setSending] = useState(false);
  const { user: me } = useAuth();

  useEffect(() => {
    if (!open) return;
    api.get("/users/directory").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  }, [open]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return users
      .filter((u) => u.id !== me?.id && u.is_active !== false)
      .filter((u) => !s || u.name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s))
      .slice(0, 30);
  }, [users, q, me]);

  const send = async (recipient) => {
    setSending(true);
    try {
      const { data } = await api.post("/pre-job-checks", {
        recipient_id: recipient.id,
        work_log_id: workLogId || null,
        obra: obra || "",
        trabajo: trabajo || "",
        data: {},
      });
      toast.success(`Pre-job check enviado a ${recipient.name}`);
      onSent && onSent(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar.");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden z-[900]" data-testid="pjc-send-dialog">
        <div className="p-5 border-b border-slate-200 bg-gradient-to-r from-[#0B2A4A] to-[#0E78C2] text-white">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[#FFC740]">Enviar</div>
          <DialogTitle asChild>
            <div className="font-display font-extrabold text-xl leading-tight mt-1 inline-flex items-center gap-2">
              <Send size={20} /> Pre-Job Check
            </div>
          </DialogTitle>
          <p className="text-xs text-white/80 mt-1">Elige a quién enviárselo. Recibirá una notificación.</p>
        </div>
        <div className="p-4">
          <input
            className="brutal-input w-full" type="text" placeholder="Buscar por nombre o email…"
            value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="pjc-send-search"
          />
        </div>
        <div className="max-h-72 overflow-y-auto border-t border-slate-200" data-testid="pjc-send-list">
          {filtered.length === 0 ? (
            <p className="p-6 text-sm text-slate-500 text-center">Sin resultados.</p>
          ) : filtered.map((u) => (
            <button
              key={u.id} type="button" disabled={sending}
              onClick={() => send(u)}
              data-testid={`pjc-send-to-${u.id}`}
              className="w-full text-left p-3 flex items-center gap-3 hover:bg-slate-50 border-b border-slate-100 disabled:opacity-60"
            >
              <div className="w-9 h-9 rounded-full bg-[#F5A623] text-white font-bold flex items-center justify-center text-sm">
                {(u.name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{u.name}</div>
                <div className="text-xs text-slate-500 truncate">{u.email} · {u.empresa || ""}</div>
              </div>
              <Send size={16} className="text-slate-400" />
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
